/**
 * @file ManualPaymentScreen.tsx
 *
 * Routed wrapper around {@link ManualPaymentForm} for the `/payments/manual`
 * route. Loads the manual gateway, seeds the form, and owns persistence: on save
 * it PUTs the gateway patch, toasts the outcome, and navigates back to the
 * Payments overview. The seed is memoized so the form's controlled state isn't
 * reset on every render.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { fetchPaymentGateways, updatePaymentGateway } from '../../api/payments';
import type { PaymentGateway } from '../../api/payments-types';
import { MANUAL_GATEWAY_ID } from '../payments-constants';
import { ManualPaymentForm } from './ManualPaymentForm';
import {
  buildManualPatch,
  deriveManualFields,
} from './manual-payment-form-utils';
import type { ManualPaymentFormFields } from './manual-payment-form-types';

/**
 * Renders the routed Manual payment screen.
 *
 * @returns The manual payment screen (with a loading fallback).
 */
export function ManualPaymentScreen() {
  const intl = useIntl();
  const navigate = useNavigate();
  const [gateway, setGateway] = useState<PaymentGateway>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  useEffect(() => {
    let active = true;
    void fetchPaymentGateways()
      .then((gateways) => {
        if (!active) return;
        setGateway(gateways.find((g) => g.id === MANUAL_GATEWAY_ID));
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const seed = useMemo(() => deriveManualFields(gateway), [gateway]);

  const goBack = (): void => {
    navigate('/payments');
  };

  const handleSubmit = async (fields: ManualPaymentFormFields): Promise<void> => {
    // The seeded manual method is loaded above, so `gateway` (with its real methodId) is present;
    // updatePaymentGateway PUTs it directly — no extra GET to re-resolve the id.
    if (!gateway) {
      setSaveError(intl.formatMessage(adminMessages.paymentsManualSaveError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    setSaving(true);
    setSaveError(undefined);
    try {
      await updatePaymentGateway(gateway, buildManualPatch(fields));
      notifySuccess(intl.formatMessage(adminMessages.toastSaved));
      goBack();
    } catch {
      setSaveError(intl.formatMessage(adminMessages.paymentsManualSaveError));
      notifyError(intl.formatMessage(adminMessages.toastError));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gv-flex gv-justify-center gv-p-lg">
        <Loader condensed />
      </div>
    );
  }

  return (
    <ManualPaymentForm
      seed={seed}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
